import React, { useState, useMemo, useEffect } from 'react'
import { Title, useDataProvider } from 'react-admin'
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@material-ui/core'
import { USER_ROLES } from '../constants'

const AUDIT_TYPES = [
  { id: 'logins', label: 'User logins', resource: 'auth/audit-logins' },
  { id: 'vnc-sessions', label: 'VNC sessions', resource: 'vnc/audit-sessions' },
  { id: 'alert-sends', label: 'Alert sends', resource: 'alerts/audit-sends' }
]

const dateCellStyle = { whiteSpace: 'nowrap' }

const AuditTypeTabs = ({ value, onChange }) => (
  <Tabs value={value} onChange={(_, v) => onChange(v)} indicatorColor="primary" textColor="primary">
    {AUDIT_TYPES.map((t) => (
      <Tab key={t.id} label={t.label} value={t.id} />
    ))}
  </Tabs>
)

const LoginsTable = ({ data }) => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>User</TableCell>
          <TableCell>Account</TableCell>
          <TableCell>Logged in at</TableCell>
          <TableCell>Event type</TableCell>
          <TableCell>IP</TableCell>
          <TableCell>User agent</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(data || []).map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              {(() => {
                const name = [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
                const email = row.email
                if (name && email) return `${name} (${email})`
                if (name) return name
                return email || row.userId || '-'
              })()}
            </TableCell>
            <TableCell>{(row.accountName || row.accountId) || '-'}</TableCell>
            <TableCell style={dateCellStyle}>{row.loggedInAt ? new Date(row.loggedInAt).toLocaleString() : '-'}</TableCell>
            <TableCell>{row.eventType || '-'}</TableCell>
            <TableCell>{row.clientIp || '-'}</TableCell>
            <TableCell>{row.userAgent || '–'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

const vncOutcome = (row) => {
  const status = row.status || ''
  const connected = row.connectedAt
  if (status === 'active') return 'Active'
  if (status === 'disconnected') return 'Completed'
  if (status === 'failed') return 'Failed'
  if (status === 'expired') return 'Expired'
  if (['pending', 'activating'].includes(status) && !connected) return 'Never connected'
  return status || '-'
}

const vncDuration = (row) => {
  const start = row.connectedAt ? new Date(row.connectedAt) : null
  const end = row.disconnectedAt ? new Date(row.disconnectedAt) : null
  if (!start || !end || end < start) return '-'
  const mins = Math.round((end - start) / 60000)
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

const formatBytesPerSecond = (value) => {
  if (value == null || value === '') return '–'

  const num = Number(value)
  if (!Number.isFinite(num) || num < 0) return '–'
  if (num < 1024) return `${Math.round(num)} B/s`
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB/s`

  return `${(num / (1024 * 1024)).toFixed(1)} MB/s`
}

const VncSessionsTable = ({ data }) => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>User</TableCell>
          <TableCell>Modality</TableCell>
          <TableCell>Requested</TableCell>
          <TableCell>Connected</TableCell>
          <TableCell>Ended</TableCell>
          <TableCell>Duration</TableCell>
          <TableCell>Peak down</TableCell>
          <TableCell>Peak up</TableCell>
          <TableCell>Outcome</TableCell>
          <TableCell>Details</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(data || []).map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              {(() => {
                const name = [row.userFirstName, row.userLastName].filter(Boolean).join(' ').trim()
                const email = row.userEmail
                if (name && email) return `${name} (${email})`
                if (name) return name
                return email || row.userId || '-'
              })()}
            </TableCell>
            <TableCell>
              {(() => {
                const siteName = row.modalitySiteName
                const serial = row.modalitySerialNumber
                if (siteName && serial) return `${siteName} (${serial})`
                if (siteName) return siteName
                return serial || row.modalityId || '-'
              })()}
            </TableCell>
            <TableCell style={dateCellStyle}>
              {row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}
            </TableCell>
            <TableCell style={dateCellStyle}>
              {row.connectedAt ? new Date(row.connectedAt).toLocaleString() : '–'}
            </TableCell>
            <TableCell style={dateCellStyle}>
              {row.disconnectedAt ? new Date(row.disconnectedAt).toLocaleString() : '–'}
            </TableCell>
            <TableCell>{vncDuration(row)}</TableCell>
            <TableCell style={dateCellStyle}>{formatBytesPerSecond(row.peakBytesPerSecDown)}</TableCell>
            <TableCell style={dateCellStyle}>{formatBytesPerSecond(row.peakBytesPerSecUp)}</TableCell>
            <TableCell>{vncOutcome(row)}</TableCell>
            <TableCell>{row.statusMessage || '–'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

const formatIncidentSummary = (incident) => {
  const serial = incident.serialNumber || incident.model || `Modality ${incident.modalityId || '?'}`
  const site = incident.siteName ? ` (${incident.siteName})` : ''
  const issue = [incident.metric, incident.severity].filter(Boolean).join(' ')
  if (!issue) return `${serial}${site}`
  return `${serial}${site}: ${issue}`
}

const AlertSendsTable = ({ data }) => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Sent at</TableCell>
          <TableCell>Account</TableCell>
          <TableCell>Schedule</TableCell>
          <TableCell>Incidents</TableCell>
          <TableCell>Recipients</TableCell>
          <TableCell>Count</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(data || []).map((row) => (
          <TableRow key={row.id}>
            <TableCell style={dateCellStyle}>
              {row.sentAt ? new Date(row.sentAt).toLocaleString() : '-'}
            </TableCell>
            <TableCell>{(row.accountName || row.accountId) || '–'}</TableCell>
            <TableCell>{row.scheduleTitle || row.scheduleId}</TableCell>
            <TableCell style={{ maxWidth: 320 }}>
              {row.incidents && row.incidents.length > 0
                ? row.incidents.map((inc, i) => (
                    <div key={inc.id || i} style={{ marginBottom: i < row.incidents.length - 1 ? 4 : 0 }}>
                      {formatIncidentSummary(inc)}
                    </div>
                  ))
                : row.incidentIds && row.incidentIds.length > 0
                  ? `Incident IDs: ${row.incidentIds.join(', ')}`
                  : '–'}
            </TableCell>
            <TableCell>
              {row.recipients && row.recipients.length > 0
                ? (() => {
                    const emailNames = row.recipients.filter(r => r.channel === 'email').map(r => r.name)
                    const smsNames = row.recipients.filter(r => r.channel === 'sms').map(r => r.name)
                    return (
                      <>
                        {emailNames.length > 0 && <div><strong>Email:</strong> {emailNames.join(', ')}</div>}
                        {smsNames.length > 0 && <div><strong>SMS:</strong> {smsNames.join(', ')}</div>}
                      </>
                    )
                  })()
                : '-'}
            </TableCell>
            <TableCell>{row.alertsCount ?? '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

export const AuditPage = () => {
  const [auditType, setAuditType] = useState('logins')
  const [filter, setFilter] = useState({})
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(25)
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const roleId = user?.roleId ?? user?.role_id
  const isSuperAdmin = roleId === USER_ROLES.SUPER_ADMIN
  const canViewAudit =
    roleId === USER_ROLES.SUPER_ADMIN || roleId === USER_ROLES.ACCOUNT_ADMIN

  const resource = useMemo(
    () => AUDIT_TYPES.find((t) => t.id === auditType)?.resource || 'auth/audit-logins',
    [auditType]
  )

  const dataProvider = useDataProvider()
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [accounts, setAccounts] = useState([])

  useEffect(() => {
    if (!isSuperAdmin) return
    dataProvider
      .getList('accounts', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'name', order: 'ASC' },
        filter: {}
      })
      .then(({ data: listData }) => setAccounts(Array.isArray(listData) ? listData : []))
      .catch(() => setAccounts([]))
  }, [isSuperAdmin, dataProvider])

  const sortField =
    auditType === 'logins' ? 'loggedInAt' : auditType === 'vnc-sessions' ? 'createdAt' : 'sentAt'

  useEffect(() => {
    if (!canViewAudit) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    dataProvider
      .getList(resource, {
        pagination: { page: page + 1, perPage },
        sort: { field: sortField, order: 'DESC' },
        filter: { ...filter }
      })
      .then(({ data: listData, total: totalCount }) => {
        setData(Array.isArray(listData) ? listData : [])
        setTotal(typeof totalCount === 'number' ? totalCount : 0)
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false))
  }, [resource, sortField, page, perPage, filter.from, filter.to, filter.accountId, canViewAudit, dataProvider])
  
  if (!canViewAudit) {
    return (
      <Box p={2}>
        <Typography color="error">You do not have permission to view the audit trail.</Typography>
      </Box>
    )
  }

  const tableProps = { data: Array.isArray(data) ? data : [] }

  return (
    <>
      <Title title="Audit trail" />
      <Box p={2}>
        <Box mb={2} display="flex" alignItems="center" flexWrap="wrap">
          <AuditTypeTabs value={auditType} onChange={(v) => { setAuditType(v); setPage(0) }} />
          <Box ml={2} display="flex" alignItems="center">
            <Typography variant="body2" style={{ marginRight: 8 }}>
              From
            </Typography>
            <input
              type="date"
              onChange={(e) => { setFilter((f) => ({ ...f, from: e.target.value || undefined })); setPage(0) }}
              value={filter.from || ''}
              style={{ marginRight: 16 }}
            />
            <Typography variant="body2" style={{ marginRight: 8 }}>
              To
            </Typography>
            <input
              type="date"
              onChange={(e) => { setFilter((f) => ({ ...f, to: e.target.value || undefined })); setPage(0) }}
              value={filter.to || ''}
            />
            {isSuperAdmin && (
              <FormControl size="small" style={{ minWidth: 200, marginLeft: 16 }}>
                <InputLabel id="audit-account-label" shrink>Account</InputLabel>
                <Select
                  labelId="audit-account-label"
                  value={filter.accountId ?? ''}
                  onChange={(e) => {
                    setFilter((f) => ({
                      ...f,
                      accountId: e.target.value ? Number(e.target.value) : undefined
                    }))
                    setPage(0)
                  }}
                  label="Account"
                  displayEmpty
                >
                  <MenuItem value="">All accounts</MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.name || `Account ${acc.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error">Failed to load audit data: {error?.message}</Typography>
        )}
        {!loading && !error && (
          <>
            {auditType === 'logins' && <LoginsTable {...tableProps} />}
            {auditType === 'vnc-sessions' && <VncSessionsTable {...tableProps} />}
            {auditType === 'alert-sends' && <AlertSendsTable {...tableProps} />}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onChangePage={(_, newPage) => setPage(newPage)}
              rowsPerPage={perPage}
              onChangeRowsPerPage={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0) }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Box>
    </>
  )
}

export default AuditPage
